"use client";
import { useState } from "react";
import Link from "next/link";
import { 
  Cpu, 
  Layers, 
  ShieldCheck, 
  Compass, 
  Eye, 
  CheckSquare, 
  Search, 
  Activity,
  ChevronRight,
  Sparkles
} from "lucide-react";
import { KineticPageShell } from "@/components/kinetic/KineticPageShell";
import { KineticWorkshopFooter } from "@/components/kinetic/KineticWorkshopFooter";

type SkillID = "diagnose" | "grill" | "triage" | "architecture" | "tdd" | "zoom";

interface SkillData {
  title: string;
  icon: any;
  metric: string;
  traditional: {
    desc: string;
    consequence: string;
  };
  governed: {
    desc: string;
    advantage: string;
  };
  lineage: string;
}

const SKILL_MAP: Record<SkillID, SkillData> = {
  diagnose: {
    title: "[diagnose] - Bug Isolation",
    icon: Compass,
    metric: "Isolate Bugs 10x Faster",
    traditional: {
      desc: "Agent relies on loose grep patterns, manually scanning folders of code files in search of bug signatures.",
      consequence: "Drives massive context window exhaustion, high LLM API costs, and high hallucination risk."
    },
    governed: {
      desc: "Ontology associates conceptual domain terms directly to their physical Go/JS AST syntax locations via symbol scanning.",
      advantage: "Exposes instant deep-linking protocols (vscode:// or cursor://) to open the exact lines in the local IDE natively."
    },
    lineage: "Entity:Term -> Relation:implemented_in -> File Lineage"
  },
  grill: {
    title: "[grill-with-docs] - Schema Challenge",
    icon: Activity,
    metric: "Zero-Decay Architecture",
    traditional: {
      desc: "Challenges design models against static, text-based ADRs and flat CONTEXT.md files.",
      consequence: "Documentation is decoupled from the physical codebase, decaying the second code is modified."
    },
    governed: {
      desc: "Statically validates structural modifications against a strongly-typed schema.yaml file.",
      advantage: "Instantly blocks relational violations (e.g. creating a Plan with no designated Curation Role) locally before staging."
    },
    lineage: "Graph -> Relation:governed_by -> Schema Policies"
  },
  triage: {
    title: "[triage] - State Verification",
    icon: CheckSquare,
    metric: "Verifiable Done Criteria",
    traditional: {
      desc: "Issues are Triaged via human check processes or simple Kanban columns on external boards.",
      consequence: "Lack of direct alignment; tickets can be closed without verifying real codebase implementation."
    },
    governed: {
      desc: "Models developer tasks (Bead, Task, Ticket) as first-class nodes in the append-only semantic graph.",
      advantage: "The fsck engine validates that a ticket cannot transition to 'verified' unless all code criteria are met."
    },
    lineage: "Ticket -> Relation:verified_by -> Code Invariant"
  },
  architecture: {
    title: "[improve-architecture] - Refactoring",
    icon: Layers,
    metric: "Topological Integrity",
    traditional: {
      desc: "Linters verify styling guidelines but are blind to broad conceptual circles and structural drift.",
      consequence: "Leads to modular leakage, high-coupling hotspots, and circular dependencies in monorepos."
    },
    governed: {
      desc: "Translates active knowledge graphs into dot files, Cypher queries, or RDF/OWL schemas.",
      advantage: "Enables executing graph-theory queries to instantly isolate orphans, high-coupling hotspots, and cycles."
    },
    lineage: "Graph-Theory -> Cypher / Graphviz DOT -> Acyclic Check"
  },
  tdd: {
    title: "[tdd] - Test-Driven Dev",
    icon: ShieldCheck,
    metric: "Machine-Readable Specs",
    traditional: {
      desc: "AI attempts to write unit tests based on abstract, loosely written human text descriptions.",
      consequence: "Requires iterative prompt tuning and guessing to align tests with real validation goals."
    },
    governed: {
      desc: "Pulls exact verification constraints straight from the machine-readable beads.json registry.",
      advantage: "Auto-scaffolds precise red-green integration tests mapped straight to structured Acceptance Criteria."
    },
    lineage: "Bead -> Property:acceptance_criteria -> Test Hook"
  },
  zoom: {
    title: "[zoom-out] - Structural Context",
    icon: Search,
    metric: "99% Context Footprint Reduction",
    traditional: {
      desc: "Agent is forced to read directories of unfamiliar modules to build a mental map of relationships.",
      consequence: "Exhausts prompt windows, slows execution, and causes cognitive overload for the model."
    },
    governed: {
      desc: "Retrieves a curated visual sub-graph showing relationships directly from the visualizer.",
      advantage: "Agent understands conceptual maps in kilobytes of clean metadata instead of megabytes of raw files."
    },
    lineage: "Visualizer -> Vis.js Graph -> Sub-graph Context Ingress"
  }
};

export default function ArticlePage() {
  const [selectedSkill, setSelectedSkill] = useState<SkillID>("diagnose");
  const skill = SKILL_MAP[selectedSkill];

  return (
    <KineticPageShell className="relative overflow-hidden">
      {/* Structural HUD grid lines */}
      <div
        className="border-kinetic-on-surface/5 pointer-events-none fixed right-0 top-0 z-0 h-screen w-1/4 border-l"
        aria-hidden
      />
      <div
        className="border-kinetic-on-surface/5 pointer-events-none fixed left-0 top-1/2 z-0 h-px w-screen border-t"
        aria-hidden
      />

      {/* Kinetic ambient glow blurs */}
      <div className="absolute top-[8%] right-[-15%] w-[500px] h-[500px] rounded-full bg-kinetic-primary-container/10 blur-[130px]" />
      <div className="absolute bottom-[25%] left-[-10%] w-[600px] h-[600px] rounded-full bg-kinetic-secondary-fixed/5 blur-[150px]" />

      <main className="relative z-10 min-h-screen pb-16 pt-28 md:pb-24 md:pt-32">
        <div className="mx-auto max-w-5xl px-4 sm:px-8">
          
          <header className="relative mb-12 pl-5 md:mb-20 md:pl-0">
            <div
              className="absolute left-0 top-0 h-full w-1 bg-kinetic-primary-container md:-left-4"
              aria-hidden
            />
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <div className="mb-2 font-mono text-xs tracking-tighter text-kinetic-secondary-fixed">
                  PROTOCOL_ID: RLM_SKILLS_SYNERGY_V1
                </div>
                <h1 className="font-headline text-4xl font-extrabold uppercase leading-none tracking-tighter text-white sm:text-5xl md:text-6xl">
                  The Cognitive Map
                </h1>
                <div className="font-headline text-lg md:text-xl font-bold uppercase tracking-wide text-kinetic-outline-variant mt-2">
                  How Governed Ontologies Supercharge AI Developer Skills
                </div>
              </div>
              <div className="text-left md:text-right font-mono text-[10px] uppercase tracking-widest text-kinetic-outline">
                <div>AUTHOR: Peter Hanssens</div>
                <div className="text-kinetic-primary-container font-bold mt-1">2 June 2026</div>
              </div>
            </div>
          </header>

          {/* Intro Section */}
          <div className="kinetic-glass-panel border-kinetic-on-surface/10 relative border-l p-6 md:p-8 mb-12">
            <div className="text-kinetic-primary-fixed/30 absolute right-0 top-0 p-2 font-mono text-[8px] tracking-tighter">
              AGENTIC_COGNITION: ENGAGED
            </div>
            <p className="text-base font-medium leading-relaxed text-kinetic-on-surface-variant md:text-lg">
              Tactical AI coding assistants are fast, but they are conceptually blind. They can build a unit test or refactor a loop, but they lack a formal, machine-readable model of the software's overarching architecture and policies. By bridging this gap with a **Governed Ontology**, we provide AI agents with a real-time **Cognitive Map**—anchoring tactical execution in strategic constraints with zero ongoing token cost.
            </p>
          </div>

          {/* Cognitive Map Console (Interactive Interface) */}
          <div className="mb-16">
            <div className="relative pl-5 md:pl-0 mb-8">
              <div
                className="absolute left-0 top-0 h-full w-0.5 bg-kinetic-secondary-fixed md:-left-3"
                aria-hidden
              />
              <div className="font-mono text-xs tracking-tighter text-kinetic-secondary-fixed uppercase">
                COGNITIVE_MAP_CONSOLE // INTERACTIVE_PLAYBOOK
              </div>
              <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white mt-1">
                Supercharging Agentic Skills
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Skill Selector Deck */}
              <div className="lg:col-span-5 space-y-3 font-mono text-[10px] uppercase tracking-wider">
                {(Object.keys(SKILL_MAP) as SkillID[]).map(skillId => {
                  const item = SKILL_MAP[skillId];
                  const Icon = item.icon;
                  const isSelected = selectedSkill === skillId;

                  return (
                    <button
                      key={skillId}
                      onClick={() => setSelectedSkill(skillId)}
                      className={`w-full border p-4 text-left transition-all duration-200 flex items-center justify-between group ${
                        isSelected
                          ? "border-kinetic-primary-container text-kinetic-primary-container bg-kinetic-surface-container-lowest"
                          : "border-kinetic-outline-variant/40 text-kinetic-on-surface-variant hover:border-kinetic-primary-container hover:text-white"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className={`h-4 w-4 shrink-0 ${isSelected ? "text-kinetic-primary-container" : "text-kinetic-outline"}`} />
                        <span className="font-bold text-xs">{item.title}</span>
                      </div>
                      <ChevronRight className={`h-3 w-3 transition-transform ${isSelected ? "translate-x-1" : "opacity-0 group-hover:opacity-100"}`} />
                    </button>
                  );
                })}
              </div>

              {/* Console Dashboard Output */}
              <div className="lg:col-span-7 border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 md:p-8 relative">
                <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[9px] text-kinetic-primary-container">
                  CONSOLE_OUTPUT: {selectedSkill.toUpperCase()}_SYNERGY
                </div>
                
                <div className="flex justify-between items-start mb-6 border-b border-kinetic-on-surface/5 pb-4">
                  <div>
                    <span className="font-mono text-[9px] text-kinetic-secondary-fixed tracking-widest block uppercase">ACTIVE_METRIC</span>
                    <span className="font-headline text-lg font-bold text-white uppercase tracking-tight flex items-center gap-1.5 mt-1">
                      <Sparkles className="h-4 w-4 text-kinetic-primary-fixed" />
                      {skill.metric}
                    </span>
                  </div>
                  <div className="text-right font-mono text-[8px] text-kinetic-outline uppercase">
                    PROVENANCE_LINEAGE:
                    <div className="text-kinetic-outline-variant text-[9px] mt-1 font-semibold">{skill.lineage}</div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Traditional Pain Point */}
                  <div className="border border-red-500/20 bg-red-950/10 p-5 rounded-xl">
                    <div className="font-mono text-[9px] font-bold text-red-400 uppercase tracking-widest mb-2 flex items-center space-x-1">
                      <span>❌ TRADITIONAL AGENT LIMITS</span>
                    </div>
                    <p className="text-xs text-kinetic-on-surface-variant leading-relaxed">
                      {skill.traditional.desc}
                    </p>
                    <div className="text-[10px] text-red-400/80 mt-2 font-mono leading-relaxed">
                      // Consequence: {skill.traditional.consequence}
                    </div>
                  </div>

                  {/* Governed Advantage */}
                  <div className="border border-emerald-500/20 bg-emerald-950/10 p-5 rounded-xl">
                    <div className="font-mono text-[9px] font-bold text-emerald-400 uppercase tracking-widest mb-2 flex items-center space-x-1">
                      <span>✅ ONTOLOGY-POWERED INGRESS</span>
                    </div>
                    <p className="text-xs text-kinetic-on-surface-variant leading-relaxed">
                      {skill.governed.desc}
                    </p>
                    <div className="text-[10px] text-emerald-400/90 mt-2 font-mono leading-relaxed">
                      // Advantage: {skill.governed.advantage}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Zero-Token AST Synchronization Show-off */}
          <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 md:p-8 mb-16 relative">
            <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[10px] text-kinetic-primary-container">
              ENGINEERING_SPOTLIGHT: ZERO_TOKEN_AST_SYNC
            </div>
            
            <h3 className="font-headline text-xl font-bold uppercase tracking-tight text-white mb-3">
              Zero-Token AST Invariance
            </h3>
            <p className="text-sm leading-relaxed text-kinetic-on-surface-variant mb-6">
              A major challenge in AI observability is cost. Running an LLM constantly to scan updated source files for minor line number shifts is extremely wasteful. 
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div className="space-y-4">
                <p className="text-xs leading-relaxed text-kinetic-outline-variant">
                  We engineered a lightweight, local parser tool hook (<code>sync-ast-lineages.js</code>) that integrates directly into the Git pre-commit workflow. 
                </p>
                <p className="text-xs leading-relaxed text-kinetic-outline-variant">
                  Whenever code changes are made, the hook walks the physical codebase, identifies shifted class and method lines via standard local AST scanning, and appends <code>update</code> logs to the <code>graph.jsonl</code> file in **under 0.2 seconds** for a cost of exactly **$0.00**.
                </p>
              </div>
              <div className="border border-kinetic-primary-fixed/20 bg-kinetic-primary-container/5 p-5 rounded-xl font-mono text-[11px] text-kinetic-on-surface-variant space-y-2">
                <div className="text-kinetic-primary-container font-bold uppercase text-[9px] tracking-widest mb-1">// GIT HOOK EXECUTION LOG</div>
                <div>$ git commit -m "refactor layouts"</div>
                <div className="text-kinetic-secondary-fixed">⚡ Running AST Shift Invariance Sync...</div>
                <div className="text-emerald-400">✓ Lineage matched. Parsed 24 JS classes in 84ms.</div>
                <div className="text-kinetic-secondary-fixed">⚡ Running local ontology validate...</div>
                <div className="text-emerald-400">✓ Validation successful! 0 errors. Commit accepted.</div>
              </div>
            </div>
          </div>

          {/* Action Call to Action */}
          <div className="border-kinetic-error/20 bg-kinetic-error-container/5 relative mt-16 border-2 border-dashed p-8 text-center max-w-3xl mx-auto">
            <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[10px] text-kinetic-primary-fixed">
              SYSTEM_BOOTSTRAP_ACTION
            </div>
            <h3 className="font-headline text-xl font-bold uppercase tracking-tight text-white mb-3">
              Scale Governed Engineering Loops
            </h3>
            <p className="text-sm leading-relaxed text-kinetic-on-surface-variant max-w-xl mx-auto mb-6">
              Ready to supercharge your AI developer assistants and enforce strict architectural gates at zero ongoing token cost? Connect with Cloud Shuttle to deploy governed ontology pipelines today.
            </p>
            <Link
              href="/contact-us"
              className="hover:bg-kinetic-primary-fixed/5 relative inline-block border border-kinetic-primary-fixed px-8 py-3 font-headline text-xs font-bold uppercase tracking-widest text-kinetic-primary-fixed transition-all"
            >
              <span
                className="absolute -left-1 -top-1 h-2 w-2 border-l-2 border-t-2 border-kinetic-primary-fixed"
                aria-hidden
              />
              <span
                className="absolute -bottom-1 -right-1 h-2 w-2 border-b-2 border-r-2 border-kinetic-primary-fixed"
                aria-hidden
              />
              BOOK_FREE_CONSULTATION
            </Link>
          </div>

        </div>
      </main>

      <div className="relative z-10">
        <KineticWorkshopFooter />
      </div>
    </KineticPageShell>
  );
}
